import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  useAssinaturasAgente,
  useEnviarBoletimAgora,
  useEnviosAgente,
  type AssinaturaAgente,
  type CanalAgente,
  type EnvioAgente,
} from "@/hooks/use-agent";
import { formatarPeriodo, type TipoBoletim } from "@shared/boletim-periodo";
import { ontemNaFazenda } from "@/lib/data-fazenda";
import { Loader2, Send } from "lucide-react";

// Histórico de envios + envio manual (Ciclo 2), embaixo da prévia na aba Boletim.
//
// ⚠️ O botão de envio é AÇÃO DE ESCRITA REAL — sai email de verdade. Por isso
// toda ação passa por confirmação nomeando o destino, e o reenvio de um período
// já entregue exige um segundo "sim": a idempotência do servidor devolve
// `ja_enviada` em vez de mandar de novo, e só `forcar` ignora a reserva.

const ROTULO_STATUS: Record<EnvioAgente["status"], { texto: string; classe: string }> = {
  enviada: { texto: "enviada", classe: "bg-sucesso-suave text-sucesso border-sucesso/30" },
  falhou: { texto: "falhou", classe: "bg-erro-suave text-erro border-erro/30" },
  pulado_vazio: { texto: "sem movimento", classe: "bg-atencao-suave text-atencao border-atencao/30" },
};

/** Rótulo de exibição do canal — nunca a chave crua ("whatsapp_gzappy") na tela. */
export const ROTULO_CANAL: Record<CanalAgente, string> = {
  email: "Email",
  whatsapp_gzappy: "WhatsApp (GZappy)",
  whatsapp_cloud: "WhatsApp (oficial)",
};

function periodoDoEnvio(e: EnvioAgente): string {
  if (!e.periodoInicio || !e.periodoFim) return "—";
  return formatarPeriodo({ inicio: e.periodoInicio, fim: e.periodoFim });
}

interface Pendente {
  assinaturaId: string;
  destino: string;
  data: string | null;
  /** Qual boletim reenviar — o semanal sai pela assinatura do diário (ver a rota). */
  tipo: TipoBoletim;
  rotuloPeriodo: string;
  forcar: boolean;
}

export function HistoricoEnvios() {
  const { toast } = useToast();
  const envios = useEnviosAgente();
  const assinaturas = useAssinaturasAgente();
  const enviar = useEnviarBoletimAgora();

  const [pendente, setPendente] = useState<Pendente | null>(null);
  const [escolhida, setEscolhida] = useState<string | null>(null);

  const ativas = (assinaturas.data ?? []).filter(a => a.ativo);
  const selecionada: AssinaturaAgente | undefined =
    ativas.filter(a => a.id === escolhida)[0] ?? ativas[0];

  /**
   * A assinatura por onde reenviar uma linha do histórico.
   *
   * O SEMANAL não tem assinatura própria (ele sai no sábado pela do diário, com
   * a capacidade trocada — ver server/routes/cron.ts), então uma linha
   * `boletim.semanal` casa com a assinatura `boletim.diario` da mesma audiência.
   * Sem isso, toda linha do consolidado apareceria com o botão desligado e o
   * título "Assinatura removida", que é mentira.
   *
   * `canal` entra no casamento porque a mesma audiência pode ter email E
   * WhatsApp ligados (migração 036): sem ele, reenviar uma linha de WhatsApp
   * poderia sair por email.
   */
  const assinaturaDoEnvio = (e: EnvioAgente): AssinaturaAgente | undefined => {
    const capacidadeDaAssinatura = e.capacidade === "boletim.semanal" ? "boletim.diario" : e.capacidade;
    return (assinaturas.data ?? []).filter(a =>
      a.agentAudienciaId === e.agentAudienciaId
      && a.capacidade === capacidadeDaAssinatura
      && a.canal === e.canal)[0];
  };

  const disparar = (p: Pendente) => {
    enviar.mutate(
      { assinaturaId: p.assinaturaId, data: p.data, tipo: p.tipo, forcar: p.forcar },
      {
        onSuccess: r => {
          if (r.desfecho === "ja_enviada") {
            // Não mandou — e diz por quê, oferecendo o reenvio explícito.
            setPendente({ ...p, forcar: true });
            return;
          }
          setPendente(null);
          if (r.desfecho === "enviada") toast({ title: `Boletim enviado para ${r.destino}` });
          else if (r.desfecho === "pulado_vazio") toast({ title: "Sem movimento no período — nada foi enviado" });
          else toast({ title: "Não consegui enviar", description: r.erro, variant: "destructive" });
        },
        onError: e => {
          setPendente(null);
          toast({
            title: "Não consegui enviar",
            description: e instanceof Error ? e.message : String(e),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="space-y-3 pt-2 border-t">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-semibold">Histórico de envios</h2>
          <p className="text-sm text-muted-foreground">O que o agente já mandou, e o que ele pulou.</p>
        </div>
        {selecionada && (
          <div className="flex items-center gap-2">
            {ativas.length > 1 && (
              <Select value={selecionada.id} onValueChange={setEscolhida}>
                <SelectTrigger className="w-40" data-testid="select-assinatura-envio">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ativas.map(a => <SelectItem key={a.id} value={a.id}>{a.audienciaNome}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPendente({
                assinaturaId: selecionada.id,
                destino: selecionada.destino,
                data: ontemNaFazenda(),
                tipo: "diario",
                rotuloPeriodo: formatarPeriodo({ inicio: ontemNaFazenda(), fim: ontemNaFazenda() }),
                forcar: false,
              })}
              data-testid="btn-enviar-ontem"
            >
              <Send className="h-4 w-4 mr-1.5" />
              Enviar ontem agora
            </Button>
          </div>
        )}
      </div>

      {envios.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : envios.isError ? (
        <p className="text-sm text-erro py-6 text-center" data-testid="erro-envios">
          {(envios.error as any)?.message ?? "Não foi possível carregar o histórico."}
        </p>
      ) : (envios.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Nada enviado ainda. Ligue a assinatura em <strong>Envio automático</strong>.
        </p>
      ) : (
        <ul className="space-y-2">
          {(envios.data ?? []).map(e => {
            // `boletim.teste` (aba Boletim → Enviar teste, Ciclo 3a) NUNCA pode
            // se misturar visualmente com o envio agendado — quem olha o
            // histórico e vê "enviada" pro dia 07/08 tem que saber na hora se
            // foi o agendado ou só um teste manual.
            const ehTeste = e.capacidade === "boletim.teste";
            const ehSemanal = e.capacidade === "boletim.semanal";
            const assinatura = ehTeste ? undefined : assinaturaDoEnvio(e);
            const rotulo = ROTULO_STATUS[e.status];
            return (
              <li key={e.id} className="rounded-md border p-2.5 flex items-start justify-between gap-2" data-testid={`envio-${e.id}`}>
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{periodoDoEnvio(e)}</span>
                    {ehTeste && (
                      <Badge variant="outline" className="text-xs bg-info-suave text-info border-info/30" data-testid={`badge-teste-${e.id}`}>
                        teste
                      </Badge>
                    )}
                    {/* Todo sábado o histórico ganha DUAS linhas por canal, com
                        períodos que terminam no mesmo dia. Sem o selo, a única
                        diferença visível seria a data de início. */}
                    {ehSemanal && (
                      <Badge variant="outline" className="text-xs font-normal" data-testid={`badge-semanal-${e.id}`}>
                        semanal
                      </Badge>
                    )}
                    <Badge variant="outline" className={`text-xs ${rotulo.classe}`}>{rotulo.texto}</Badge>
                    <span className="text-xs text-muted-foreground">{ROTULO_CANAL[e.canal] ?? e.canal}</span>
                  </div>
                  <p className="text-xs text-muted-foreground break-all">{e.destino}</p>
                  {e.erro && <p className="text-xs text-erro break-words">{e.erro}</p>}
                </div>
                {/* Teste não tem "reenviar agora" — o caminho é a aba Boletim, não uma assinatura. */}
                {!ehTeste && (
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!assinatura}
                    title={assinatura ? "Enviar este boletim agora" : "Assinatura removida"}
                    onClick={() => assinatura && setPendente({
                      assinaturaId: assinatura.id,
                      destino: e.destino,
                      data: e.periodoInicio,
                      tipo: ehSemanal ? "semanal" : "diario",
                      rotuloPeriodo: periodoDoEnvio(e),
                      forcar: false,
                    })}
                    data-testid={`btn-reenviar-${e.id}`}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AlertDialog open={pendente !== null} onOpenChange={aberto => { if (!aberto) setPendente(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendente?.forcar ? "Esse boletim já foi enviado" : "Enviar agora?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendente?.forcar
                ? `O boletim de ${pendente?.rotuloPeriodo} já saiu para ${pendente?.destino}. Reenviar mesmo assim?`
                : `O boletim de ${pendente?.rotuloPeriodo} será enviado para ${pendente?.destino} agora.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enviar.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={enviar.isPending}
              onClick={ev => { ev.preventDefault(); if (pendente) disparar(pendente); }}
              data-testid="btn-confirmar-envio"
            >
              {enviar.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {pendente?.forcar ? "Reenviar mesmo assim" : "Enviar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
